/* eslint-disable max-lines */
/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import React, { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { gql } from "graphql-tag";
import { FormControl, FormControlLabel, Paper, Switch, Tooltip, Typography, Grid } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import ErrorMessage from "components/error-message";
import { Delta } from "components/message-editor/types";
import { Viewer } from "components/auth/types";
import { emptyDelta } from "components/message-editor";
import {
  getInterestListById,
  getInterestListByIdVariables,
  Feature,
  getInterestListById_interestList_property as PropertyType,
  getInterestListById_interestList_property_propertyUseCases as useCase,
  getTemplateGroup_templateGroup as Template,
  TemplateType,
} from "types/gql-types";
import { LoadingSpinner } from "components/graphql-views";
import NotFound from "pages/not-found";
import { SelectAutocomplete } from "components/haven-design/form/select";
import { ChannelForm } from "./channel-form";
import SubmitHandler from "./submit-handler";
import { CopyConfirmationButton } from "./copy-confirmation";


const SMS_CHARACTER_LIMIT = 280;

export type Props = {
  isSending: boolean;
  handleSubmit?(values: SubmitValues, resetForm?: () => void): void;

  /** Skip the automatic post-submit form reset — the caller resets via the second
   * `handleSubmit` argument once the send is actually confirmed (e.g. Select All). */
  deferReset?: boolean;
  smsValue?: Delta;
  template?: Template | null;
  smsId?: string | null;
  letterId?: string | null;
  emailId?: string | null;
  letterValue?: Delta;
  emailValue?: Delta;
  emailSubject?: string | null;
  emailBccAddress?: string | null;
  onRequestClose?(): void;
  viewer: Viewer;
  currentListId: string;
  readOnly?: boolean;
  isHidingSubmitBtns?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (params: any) => void;
  errorMessages?: {
    email: string;
    sms: string;
    letter: string;
    emailSubject?: string;
    smsUseCase?: string;
  };
  allowCopy?: boolean;
}

export type SubmitValues = {
  emailEnabled: boolean;
  emailBody: string;
  emailSubject: string;
  emailRespondToEnabled?: boolean;
  emailBccAddress: string | null;
  smsEnabled: boolean;
  letterEnabled: boolean;
  smsPropertyUseCaseId: string | null;
  smsBody: string;
  letterBody: string;
}

export const INTEREST_LIST = gql`
  query getInterestListById($id: ID!) {
    interestList(id: $id) {
      id
      property {
        id
        name
        respondToEmail
        respondToEmailEnabled
        features {
          id
          name
        }
        propertyUseCases {
          id
          smsPrefix
          active
          useCase {
            friendlyName
          }
        }
      }
    }
  }
`;
const SwitchWithLabel: React.FC<{
  id?: string,
  checked: boolean,
  onChangeAction: () => void,
  label: string,
  name: string,
}> = ({ id, checked, onChangeAction, label, name }) =>
  <FormControl>
    <FormControlLabel
      id={id}
      name={name}
      onChange={onChangeAction}
      control={<Switch checked={checked} />}
      label={label} />
  </FormControl>;

const templateChannelEnabled = (template: Template | null | undefined, channel: TemplateType) => {
  if (!template) return true;
  const channelTemplate = template.templates.find(({ type }) => type === channel);
  if (!channelTemplate) return false;
  return !channelTemplate.isEmpty;
};

const getFriendlyName = (propertyName:string, value: useCase | null):string => {
  if (!value) {
    return "Haven Connect";
  }

  return value.smsPrefix || propertyName;
};

const getDefaultUseCase = (useCases:useCase[]):useCase => useCases.find((item) => item.useCase.friendlyName === "Account Notifications") ?? useCases[0];

const TemplateFormContent: React.FC<Props & {property: PropertyType}> = ({
  allowCopy = true,
  ...props
}) => {
  const activeUseCases = props.property.propertyUseCases.filter((item) => item.active);
  const hasUseCases = activeUseCases.length > 0;

  const { template, property: { respondToEmail, respondToEmailEnabled, features } } = props;

  const showLetter = features.map(x => x.name).includes(Feature.SEND_LETTERS);
  const subjectError = props.errorMessages?.emailSubject || "";
  const smsUseCaseError = props.errorMessages?.smsUseCase || "";

  const [smsValue, setSmsValue] = useState<Delta>(emptyDelta);
  const [letterValue, setLetterValue] = useState<Delta>(emptyDelta);
  const [emailValue, setEmailValue] = useState<Delta>(emptyDelta);
  const [emailSubject, setEmailSubject] = useState<string>("");
  const [emailBccAddress, setEmailBccAddress] = useState<string>("");
  const [smsEnabled, setSmsEnabled] = useState<boolean>(true);
  const [emailEnabled, setEmailEnabled] = useState<boolean>(true);
  const [letterEnabled, setLetterEnabled] = useState<boolean>(showLetter);
  const [defaultEmailEnabled, setDefaultEmailEnabled] = useState<boolean>(false);
  const [selectedUseCase, setSelectedUseCase] =
    useState<useCase | null>(
      hasUseCases ? getDefaultUseCase(activeUseCases) : null,
    );

  // eslint-disable-next-line max-len
  const smsTitle = `All text messages will begin with "${getFriendlyName(props.property.name, selectedUseCase)}:" due to regulatory requirements`;

  const changeProps = {
    emailEnabled,
    emailBody: emailValue,
    emailSubject,
    emailBccAddress,
    emailRespondToEnabled: defaultEmailEnabled,
    letterEnabled,
    letterBody: letterValue,
    smsEnabled,
    smsBody: smsValue,
    smsPropertyUseCaseId: selectedUseCase?.id ?? null,
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onChange = (updates: any) => {
    if (props.onChange) {
      props.onChange({
        ...changeProps,
        ...updates,
      });
    }
  };

  // only if template has changed we want to update message values
  useEffect(() => {
    setSmsValue(props.smsValue || emptyDelta);
    setSmsEnabled(templateChannelEnabled(template, TemplateType.SMS));
    setEmailValue(props.emailValue || emptyDelta);
    setEmailEnabled(templateChannelEnabled(template, TemplateType.EMAIL));
    setEmailSubject(props.emailSubject || "");
    setEmailBccAddress(props.emailBccAddress || "");
    setLetterValue(props.letterValue || emptyDelta);
    setLetterEnabled(templateChannelEnabled(template, TemplateType.LETTER) && showLetter);
  }, [template, features]);

  useEffect(() => {
    setDefaultEmailEnabled(respondToEmailEnabled || false);
  }, [respondToEmail]);

  const setSubject = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailSubject(e.target.value);
  };

  const copyToEmail = () => {
    setEmailEnabled(true);
    setEmailValue(letterValue);
    onChange({ emailBody: letterValue, emailEnabled: true });
  };

  const copyToLetter = () => {
    setLetterEnabled(true);
    setLetterValue(emailValue);
    onChange({ letterBody: emailValue, letterEnabled: true });
  };


  const propsToSubmit = {
    smsValue,
    setSmsValue,
    emailValue,
    setEmailValue,
    emailSubject,
    setEmailSubject,
    emailBccAddress,
    setEmailBccAddress,
    smsEnabled,
    setSmsEnabled,
    emailEnabled,
    setEmailEnabled,
    letterValue,
    setLetterValue,
    letterEnabled,
    defaultEmailEnabled,
    smsPropertyUseCaseId: selectedUseCase?.id ?? null,
    smsPropertyHasUseCases: hasUseCases,
  };

  const renderLetter = () => {
    if (!showLetter) return null;
    if (!letterEnabled) return null;

    return (
      <>
        <Grid size={{ xs: 12 }}>
          <Typography variant="h6">
            Letter message:
          </Typography>
          {allowCopy && <CopyConfirmationButton
            hidden={!letterEnabled}
            context="Email"
            onCopy={copyToEmail}
            valueToReplace={emailValue} />}
        </Grid>
        {props.errorMessages?.letter && <Grid size={{ xs: 12 }}>
          <ErrorMessage errorMsg={props.errorMessages.letter}/>
        </Grid>}
        <ChannelForm
          placeholder="Letter Body"
          typePrefix={TemplateType.LETTER}
          setMessage={(val) => {
            setLetterValue(val);
            onChange({ letterBody: val });
          }}
          messageValue={letterValue}
          readOnly={props.readOnly}
        />
        <Grid size={{ xs: 12 }}><hr/></Grid>
      </>
    );
  };

  return (
    <>
      <div style={{ position: "sticky", top: -10, zIndex: 1, margin: "0 -5px" }}>
        <Paper>
          <div style={{ padding: 5 }}>
            <Grid container spacing={1}>
              <Grid size={{ xs: 6, md: 4 }}>
                <SwitchWithLabel
                  id="send-email"
                  onChangeAction={() => {
                    const bEmailEnabled = !emailEnabled;
                    setEmailEnabled(bEmailEnabled);
                    if (props.onChange) {
                      props.onChange({
                        ...changeProps,
                        emailEnabled: bEmailEnabled,
                      });
                    }
                  }}
                  label="Send an Email"
                  name="Send-Email"
                  checked={emailEnabled}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 4 }}>
                <SwitchWithLabel
                  id="send-text"
                  onChangeAction={() => {
                    const bSmsEnabled = !smsEnabled;
                    setSmsEnabled(bSmsEnabled);
                    onChange({ smsEnabled: bSmsEnabled });
                  }}
                  label="Send a Text"
                  name="Send-Text"
                  checked={smsEnabled}
                />
              </Grid>
              {showLetter && (<Grid size={{ xs: 6, md: 4 }}>
                <SwitchWithLabel
                  id="send-letter"
                  onChangeAction={() => {
                    const bLetterEnabled = !letterEnabled;
                    setLetterEnabled(bLetterEnabled);
                    onChange({ letterEnabled: bLetterEnabled });
                  }}
                  label="Send a Letter"
                  name="Send-Letter"
                  checked={letterEnabled}
                /></Grid>)}
            </Grid>
          </div>
        </Paper>
      </div>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }} style={{ height: 0 }}><br/></Grid>
        {emailEnabled && (
          <>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6">
          Email Message:
              </Typography>
              {allowCopy && <CopyConfirmationButton
                hidden={!letterEnabled}
                context="Letter"
                onCopy={copyToLetter}
                valueToReplace={letterValue} /> }

            </Grid>
            {props.errorMessages?.email && <Grid size={{ xs: 12 }}>
              <ErrorMessage errorMsg={props.errorMessages.email}/>
            </Grid>}
            <ChannelForm
              placeholder="Email Body"
              subject={emailSubject}
              setSubject={(event) => {
                setSubject(event);
                onChange({ emailSubject: event.target.value });
              }}
              subjectError={Boolean(subjectError)}
              subjectHelperText={subjectError}
              typePrefix={TemplateType.EMAIL}
              setMessage={(message) => {
                setEmailValue(message);
                onChange({ emailBody: message });
              }}
              messageValue={emailValue}
              respondToEmail={respondToEmail}
              respondToEmailEnabled={defaultEmailEnabled}
              setDefaultEmailEnabled={() => {
                const bDefaultEmailEnabled = !defaultEmailEnabled;
                setDefaultEmailEnabled(bDefaultEmailEnabled);
                onChange({ emailRespondToEnabled: bDefaultEmailEnabled });
              }}
              emailBccAddress={emailBccAddress}
              setEmailBccAddress={(e: React.ChangeEvent<HTMLInputElement>) => {
                setEmailBccAddress(e.target.value);
                onChange({ emailBccAddress: e.target.value });
              }}
              readOnly={props.readOnly}
            />
            <Grid size={{ xs: 12 }}><hr/></Grid>
          </>
        )}

        {smsEnabled && (
          <>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6">
                Text Message <Tooltip title={smsTitle}>
                  <InfoIcon fontSize="small" />
                </Tooltip> :
              </Typography>
            </Grid>
            {hasUseCases && (
              <Grid size={{ xs: 12 }}>
                <SelectAutocomplete
                  label="Use Case"
                  required
                  error={Boolean(smsUseCaseError)}
                  helperText={smsUseCaseError}
                  options={activeUseCases.map(puc => puc.useCase.friendlyName)}
                  value={selectedUseCase?.useCase.friendlyName}
                  margin="none"
                  onChange={(_, value) => {
                    const selected = activeUseCases
                      .find(puc => puc.useCase.friendlyName === value) ?? null;
                    setSelectedUseCase(selected);
                    onChange({ smsPropertyUseCaseId: selected?.id });
                  }
                  } />
              </Grid>
            )}
            {props.errorMessages?.sms && <Grid size={{ xs: 12 }}>
              <ErrorMessage errorMsg={props.errorMessages.sms}/>
            </Grid>}
            <ChannelForm
              placeholder="Message Body"
              typePrefix={TemplateType.SMS}
              setMessage={(msg) => {
                setSmsValue(msg);
                onChange({ smsBody: msg });
              }}
              messageValue={smsValue}
              characterLimit={SMS_CHARACTER_LIMIT}
              readOnly={props.readOnly}
              short
            />
            <Grid size={{ xs: 12 }}><hr/></Grid>
          </>
        )}

        {renderLetter()}
      </Grid>
      {!emailEnabled && !smsEnabled && !letterEnabled && <hr/>}
      <SubmitHandler {...{ ...props, ...propsToSubmit }}/>
    </>
  );
};
export const TemplateForm: React.FC<Props> = (props) => {
  const { data, loading } = useQuery<getInterestListById,
  getInterestListByIdVariables>(INTEREST_LIST, {
    fetchPolicy: "cache-first",
    variables: { id: props.currentListId },
  });

  if (loading || props.isSending) return <LoadingSpinner />;
  if (!data) return <NotFound />;
  const { interestList: { property } } = data;

  return <TemplateFormContent {...props} property={property}/>;
};
